import { useMemo } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, ReferenceDot } from "recharts";
import { fmtUsd } from "@/lib/api";

const UP = "#10B981", DOWN = "#EF4444";

// Custom bar shape: wick (low→high) + body (open→close). Recharts gives us the
// pixel box for the [low, high] range bar, so we interpolate body edges inside it.
const CandleShape = (props) => {
  const { x, y, width, height, payload } = props;
  if (!payload || height == null || !Number.isFinite(y)) return null;
  const { o, h, l, c } = payload;
  const up = c >= o;
  const color = up ? UP : DOWN;
  const range = h - l || 1e-9;
  const yOf = (v) => y + ((h - v) / range) * height;
  const bodyTop = yOf(Math.max(o, c));
  const bodyBot = yOf(Math.min(o, c));
  const bodyH = Math.max(1, bodyBot - bodyTop);
  const cx = x + width / 2;
  const bw = Math.max(2, width * 0.7);
  return (
    <g className="candle" data-dir={up ? "up" : "down"}>
      <line x1={cx} x2={cx} y1={y} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={up ? color : color} stroke={color} rx={1}
            style={{ filter: payload.live ? `drop-shadow(0 0 4px ${color})` : undefined }} />
    </g>
  );
};

const CandleTip = ({ active, payload, labelFormatter }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const up = d.c >= d.o;
  const digits = d.c < 1 ? 6 : 2;
  return (
    <div className="rounded-lg px-3 py-2 text-xs" style={{ background: "#0F141C", border: "1px solid #1E293B" }} data-testid="candle-tooltip">
      <div className="text-slate-400 mb-1">{labelFormatter(d.t)}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono-ticker">
        <span className="text-slate-500">Open</span><span>{fmtUsd(d.o, { digits })}</span>
        <span className="text-slate-500">High</span><span className="text-emerald-300">{fmtUsd(d.h, { digits })}</span>
        <span className="text-slate-500">Low</span><span className="text-red-300">{fmtUsd(d.l, { digits })}</span>
        <span className="text-slate-500">Close</span><span className={up ? "text-emerald-300" : "text-red-300"}>{fmtUsd(d.c, { digits })}</span>
        {d.v != null && <><span className="text-slate-500">Volume</span><span>{d.v.toFixed(4)}</span></>}
      </div>
    </div>
  );
};

// Indicator overlays computed from closes (SMA20/50, Bollinger 20±2σ)
export const withOverlays = (data) => {
  const closes = data.map((d) => d.c);
  const sma = (n, i) => (i < n - 1 ? null : closes.slice(i - n + 1, i + 1).reduce((a, b) => a + b, 0) / n);
  return data.map((d, i) => {
    const m20 = sma(20, i), m50 = sma(50, i);
    let bbUp = null, bbLo = null;
    if (m20 != null) {
      const sl = closes.slice(i - 19, i + 1);
      const sd = Math.sqrt(sl.reduce((a, b) => a + (b - m20) ** 2, 0) / 20);
      bbUp = m20 + 2 * sd; bbLo = m20 - 2 * sd;
    }
    return { ...d, ma20: m20, ma50: m50, bbUp, bbLo };
  });
};

export const Candles = ({ data, height = 320, timeFormatter, showVolume = false, refLine, showMA = false, showBB = false, markers = null, testid = "candle-chart" }) => {
  const rows = useMemo(() => (showMA || showBB ? withOverlays(data) : data).map((d) => ({ ...d, range: [d.l, d.h] })), [data, showMA, showBB]);
  const [lo, hi] = useMemo(() => {
    if (!rows.length) return [0, 1];
    const l = Math.min(...rows.map((d) => d.l)), h = Math.max(...rows.map((d) => d.h));
    const pad = Math.max((h - l) * 0.12, h * 0.00005);
    return [l - pad, h + pad];
  }, [rows]);
  const last = rows[rows.length - 1];

  return (
    <div style={{ width: "100%", height }} data-testid={testid}>
      <ResponsiveContainer>
        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
          <XAxis dataKey="t" tickFormatter={timeFormatter} stroke="#64748b" fontSize={10} tickLine={false} minTickGap={50} />
          <YAxis domain={[lo, hi]} stroke="#64748b" fontSize={10} width={80} tickFormatter={(v) => fmtUsd(v, { digits: v < 1 ? 6 : 2 })} />
          {showVolume && <YAxis yAxisId="vol" orientation="right" hide domain={[0, (max) => max * 4]} />}
          <Tooltip content={<CandleTip labelFormatter={timeFormatter} />} cursor={{ stroke: "#334155", strokeDasharray: "3 3" }} />
          {refLine != null && <ReferenceLine y={refLine} stroke="#64748b" strokeDasharray="4 4" />}
          {last && <ReferenceLine y={last.c} stroke={last.c >= last.o ? UP : DOWN} strokeDasharray="2 2" strokeOpacity={0.7} />}
          {showVolume && <Bar yAxisId="vol" dataKey="v" isAnimationActive={false} fill="#334155" opacity={0.5} />}
          <Bar dataKey="range" shape={<CandleShape />} isAnimationActive={false} />
          {markers?.support && <ReferenceLine y={markers.support} stroke="#10B981" strokeDasharray="4 4" label={{ value: "S", fill: "#10B981", fontSize: 10, position: "right" }} />}
          {markers?.resistance && <ReferenceLine y={markers.resistance} stroke="#EF4444" strokeDasharray="4 4" label={{ value: "R", fill: "#EF4444", fontSize: 10, position: "right" }} />}
          {markers?.entry && <ReferenceDot x={markers.entry.t} y={markers.entry.p} r={7} fill="#10B981" stroke="#022c22" strokeWidth={2} label={{ value: "BUY", fill: "#6EE7B7", fontSize: 10, position: "bottom" }} />}
          {markers?.exit && <ReferenceDot x={markers.exit.t} y={markers.exit.p} r={7} fill="#F59E0B" stroke="#451a03" strokeWidth={2} label={{ value: "SELL", fill: "#FCD34D", fontSize: 10, position: "top" }} />}
          {showMA && <Line type="monotone" dataKey="ma20" stroke="#06B6D4" strokeWidth={1.3} dot={false} isAnimationActive={false} connectNulls />}
          {showMA && <Line type="monotone" dataKey="ma50" stroke="#8B5CF6" strokeWidth={1.3} dot={false} isAnimationActive={false} connectNulls />}
          {showBB && <Line type="monotone" dataKey="bbUp" stroke="#8B5CF6" strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} connectNulls />}
          {showBB && <Line type="monotone" dataKey="bbLo" stroke="#8B5CF6" strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} connectNulls />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
